// ReturnConfirmationModal.jsx
import React, { useState, useEffect, useMemo } from "react";
import ReactModal from "react-modal";
import axios from "axios";
import "../calendar.css";

ReactModal.setAppElement("#root");

/* ──────────────────────────────
 *  Re-usable UI helpers
 * ──────────────────────────────*/
const SectionCard = ({ title, children, className = "" }) => (
  <section
    className={`bg-white rounded-xl border border-gray-200 shadow-lg p-6 ${className}`}
  >
    {title && (
      <h3 className="text-gray-700 font-semibold text-lg mb-6">{title}</h3>
    )}
    {children}
  </section>
);

const Divider = ({ className = "" }) => (
  <hr className={`my-8 border-t border-gray-300 ${className}`} />
);

const PillButton = ({
  active,
  children,
  className = "",
  type = "button",
  ...props
}) => (
  <button
    type={type}
    {...props}
    className={`px-6 py-2 rounded-full font-semibold transition focus:outline-none ${
      active
        ? "bg-joule text-white shadow-md"
        : "bg-gray-200 text-gray-700 hover:bg-joule hover:text-white"
    } ${className}`}
  >
    {children}
  </button>
);

const Counter = ({ value, onChange, min = 0, max, className = "" }) => {
  const dec = () => onChange(Math.max(value - 1, min));
  const inc = () =>
    onChange(typeof max === "number" ? Math.min(value + 1, max) : value + 1);

  return (
    <div className={`inline-flex items-center ${className}`}>
      <button
        type="button"
        onClick={dec}
        className="h-8 w-8 flex items-center justify-center rounded-l-full bg-gray-200 hover:bg-joule hover:text-white font-bold"
      >
        –
      </button>
      <input
        readOnly
        value={value}
        className="w-12 h-8 text-center border-t border-b border-gray-300 font-semibold"
      />
      <button
        type="button"
        onClick={inc}
        className="h-8 w-8 flex items-center justify-center rounded-r-full bg-gray-200 hover:bg-joule hover:text-white font-bold"
      >
        +
      </button>
    </div>
  );
};

/* ──────────────────────────────────────────────────────────────
 *  ReturnConfirmationModal – main component
 * ──────────────────────────────────────────────────────────────*/
const modalStyles = {
  content: {
    maxWidth: "1000px",
    width: "90%",
    margin: "auto",
    padding: "40px",
    borderRadius: "12px",
    border: "4px solid #374151",
    boxShadow: "0 12px 24px rgba(0, 0, 0, 0.6)",
    overflow: "auto",
  },
  overlay: {
    backgroundColor: "rgba(0, 0, 0, 0.7)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    padding: "20px",
    zIndex: 1000,
  },
};

/* ---------- PRICE CONSTANTS ---------- */
// ► Fill in NOK values when you get them
const ACCESSORY_PRICES = {
  charger: 2300,
  adapter: 475,
  snakeLock: 1199,
  foldingLock: 1649,
  lockKey: 299,
  batteryKey: 299,
  reTyreSkins: 799,
  keyChain: 150,
};

// Labour is a flat hourly rate (inc. VAT)
const LABOUR_RATE = 1200;

// 10-minute increments (1 / 6 hour) – 0 → 1.5 h, i.e. 0–90 min, 10 buttons
const labourOptions = Array.from({ length: 10 }, (_, i) => i / 6);

const ReturnConfirmationModal = ({
  isOpen,
  toggleModalVisibility,
  data,
  markAsReturned,
  markAsRepaired,
}) => {
  //Loading state
  const [isSubmitting, setIsSubmitting] = useState(false);

  /* ────────────── state – missing items ────────────── */
  const [returnOption, setReturnOption] = useState("returnRepair");
  const [isMissing, setIsMissing] = useState(true);
  const [note, setNote] = useState("");
  const [missingCounts, setMissingCounts] = useState({
    charger: 0,
    adapter: 0,
    snakeLock: 0,
    foldingLock: 0,
    lockKey: 0,
    batteryKey: 0,
    reTyreSkins: 0,
    keyChain: 0,
  });
  const [originalAccessories, setOriginalAccessories] = useState({});

  /* ────────────── state – parts & labour ────────────── */
  const [groupedParts, setGroupedParts] = useState({});
  const [expandedGroups, setExpandedGroups] = useState({});
  const [selectedParts, setSelectedParts] = useState([]);
  const [labourHours, setLabourHours] = useState(0);
  const [labourNote, setLabourNote] = useState("");

  /* ────────────── fetch accessories + parts ────────────── */
  useEffect(() => {
    if (!data?.serialNumber) return;

    axios
      .get(`/api/inventory/${data.serialNumber}`)
      .then((res) => {
        if (res.data?.accessories) setOriginalAccessories(res.data.accessories);

        const modelCode = res.data?.model?.SKU?.slice(0, 6);
        if (!modelCode) return;
        return axios.get(`/api/service-parts/${modelCode}`);
      })
      .then((partsRes) => {
        if (!partsRes) return;
        const grouped = partsRes.data.reduce((acc, p) => {
          acc[p.groupCode] = acc[p.groupCode] || [];
          acc[p.groupCode].push(p);
          return acc;
        }, {});
        setGroupedParts(grouped);
      })
      .catch((err) => console.error("[ReturnModal] fetch error:", err));
  }, [data.serialNumber]);

  /* ────────────── helpers ────────────── */
  const handleMissingSelect = (missing) => {
    setIsMissing(missing);
    if (!missing) {
      setMissingCounts({
        charger: 0,
        adapter: 0,
        snakeLock: 0,
        foldingLock: 0,
        lockKey: 0,
        batteryKey: 0,
        reTyreSkins: 0,
        keyChain: 0,
      });
    }
  };

  const toggleGroup = (code) =>
    setExpandedGroups((p) => ({ ...p, [code]: !p[code] }));

  const handlePartSelection = (part, qty) => {
    setSelectedParts((prev) => {
      const idx = prev.findIndex((p) => p.productNo === part.productNo);
      if (qty === 0) return prev.filter((p) => p.productNo !== part.productNo);
      if (idx > -1) {
        const next = [...prev];
        next[idx].quantity = qty;
        return next;
      }
      return [...prev, { ...part, quantity: qty }];
    });
  };

  /* ────────────── derived ────────────── */
  const accessoriesList = useMemo(
    () => [
      {
        key: "charger",
        label: "Charger",
        description: "Lader",
        original: originalAccessories.charger || 0,
      },
      {
        key: "adapter",
        label: "Adapter",
        description: "Ladeadapter",
        original: originalAccessories.adapter || 0,
      },
      {
        key: "snakeLock",
        label: "Snake Lock",
        description: "Kjettinglås",
        original: originalAccessories.snakeLock || 0,
      },
      {
        key: "foldingLock",
        label: "Folding Lock",
        description: "Foldelås",
        original: originalAccessories.foldingLock || 0,
      },
      {
        key: "lockKey",
        label: "Lock Key",
        description: "Nøkkel til lås",
        original: originalAccessories.lockKey || 0,
      },
      {
        key: "batteryKey",
        label: "Battery Key",
        description: "Nøkkel til batteri",
        original: originalAccessories.batteryKey || 0,
      },
      {
        key: "reTyreSkins",
        label: "Retyre Skins",
        description: "Retyre vinterskins",
        original: originalAccessories.reTyreSkins || 0,
      },
      {
        key: "keyChain",
        label: "Key Chain",
        description: "Nøkkelring",
        original: originalAccessories.keyChain || 0,
      },
    ],
    [originalAccessories]
  );

  /* ────────────── submit ────────────── */
  const handleFormSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    const missingLines = Object.entries(missingCounts)
      .filter(([, qty]) => qty > 0)
      .map(([key, qty]) => {
        const meta = accessoriesList.find((a) => a.key === key);
        const price = ACCESSORY_PRICES[key] ?? 0;
        return {
          lineType: "missingItem",
          productName: meta?.label || key,
          productDescription: meta?.description || key,
          quantity: qty,
          salesPriceIncVat: price,
          lineTotal: price * qty,
          status: "open",
        };
      });

    const partLines = selectedParts.map((p) => ({
      lineType: "part",
      productNo: p.productNo,
      productName: p.productName,
      productDescription: p.productDescription,
      quantity: p.quantity,
      salesPriceIncVat: p.salesPriceIncVat,
      lineTotal: p.salesPriceIncVat * p.quantity,
      status: "open",
    }));

    const labourLines =
      labourHours > 0
        ? [
            {
              lineType: "labour",
              productName: "Arbeidstid mekaniker",
              hours: labourHours,
              quantity: 1,
              salesPriceIncVat: LABOUR_RATE,
              lineTotal: LABOUR_RATE * labourHours,
              status: "open",
              note: labourNote,
            },
          ]
        : [];

    const caseLines = [...missingLines, ...partLines, ...labourLines];
    const shouldCreateCase = caseLines.length > 0;

    try {
      if (shouldCreateCase) {
        await axios.post("/api/customer-cases", {
          serialNumber: data.serialNumber,
          subscriptionId: data.subscriptionId,
          orderId: data.orderId,
          fullName: `${data.firstName} ${data.lastName}`,
          phone: data.phone,
          email: data.email,
          source: "Return Inspection",
          note,
          caseLines,
        });
      }

      await axios.post(`/api/mark-as-returned/${data.serialNumber}`);
      await markAsReturned(data._id);

      if (returnOption === "inStock") {
        await axios.post(`/api/set-in-stock/${data.serialNumber}`);
        await markAsRepaired(data._id);
        await axios.put(
          `/api/inventory/${data.serialNumber}/operationalStatus`,
          {
            operationalStatus: "in stock",
          }
        );
      } else {
        await axios.put(
          `/api/inventory/${data.serialNumber}/operationalStatus`,
          {
            operationalStatus: "repair-returned",
          }
        );
      }

      toggleModalVisibility();
    } catch (err) {
      console.error("[ReturnModal] submit error:", err);
    } finally {
      setIsSubmitting(false); // ← hide spinner no matter what
    }
  };

  const hasMissing = Object.values(missingCounts).some((q) => q > 0);
  const isSubmitDisabled =
    isSubmitting || // ← new guard
    (isMissing && // existing logic
      !hasMissing &&
      selectedParts.length === 0 &&
      labourHours === 0);

  /* ────────────── render ────────────── */
  return (
    <ReactModal
      isOpen={isOpen}
      onRequestClose={toggleModalVisibility}
      contentLabel="Return Confirmation"
      style={modalStyles}
    >
      <div className="text-center mb-8">
        <h2 className="text-gray-900 text-2xl font-semibold mb-6">
          Return Confirmation – Bike {data.serialNumber}
        </h2>

        <form
          onSubmit={handleFormSubmit}
          className="bg-white rounded px-8 pt-6 pb-8 text-base"
        >
          {/* ─────── Bike status ─────── */}
          <SectionCard title="Set Bike Status">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {[
                { val: "inStock", label: "Set in Stock" },
                { val: "returnRepair", label: "Put in Repair" },
              ].map((opt) => (
                <PillButton
                  key={opt.val}
                  active={returnOption === opt.val}
                  onClick={() => setReturnOption(opt.val)}
                  className="w-full text-lg"
                  disabled={opt.val === "inStock"}
                >
                  {opt.label}
                </PillButton>
              ))}
            </div>
          </SectionCard>

          <Divider />

          {/* ─────── Missing items ─────── */}
          <SectionCard title="Missing Items">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-6">
              <PillButton
                active={!isMissing}
                onClick={() => handleMissingSelect(false)}
                className="w-full text-lg"
              >
                Nothing is Missing
              </PillButton>
              <PillButton
                active={isMissing}
                onClick={() => handleMissingSelect(true)}
                className="w-full text-lg"
              >
                Something is Missing
              </PillButton>
            </div>

            {isMissing && (
              <>
                <div className="overflow-x-auto">
                  <table className="min-w-full border-collapse text-center rounded-t-md overflow-hidden">
                    <thead className="bg-gray-700 text-white">
                      <tr>
                        <th className="px-4 py-3 font-semibold text-sm uppercase tracking-wide">
                          Accessory
                        </th>
                        <th className="px-4 py-3 font-semibold text-sm uppercase tracking-wide">
                          Missing
                        </th>
                        <th className="px-4 py-3 font-semibold text-sm uppercase tracking-wide">
                          Previously Sent Out
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {accessoriesList.map(({ key, label, original }, idx) => (
                        <tr
                          key={key}
                          className={`border-b ${
                            idx % 2 === 1 ? "bg-gray-50" : ""
                          }`}
                        >
                          <td className="px-4 py-3 border-r">{label}</td>
                          <td className="px-4 py-3 border-r">
                            <Counter
                              value={missingCounts[key]}
                              onChange={(qty) =>
                                setMissingCounts((p) => ({ ...p, [key]: qty }))
                              }
                            />
                          </td>
                          <td className="px-4 py-3 font-extrabold">
                            {original}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                <div className="bg-gray-50 p-4 rounded-md border border-gray-200 mt-6">
                  <label
                    htmlFor="note"
                    className="block text-gray-700 text-sm font-semibold mb-2"
                  >
                    Note
                  </label>
                  <textarea
                    id="note"
                    value={note}
                    onChange={(e) => setNote(e.target.value)}
                    placeholder="Additional notes..."
                    className="border-gray-600 shadow-sm appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:border-joule focus:ring-1 focus:ring-joule"
                  />
                </div>
              </>
            )}
          </SectionCard>

          <Divider />

          {/* ─────── Parts ─────── */}
          <SectionCard title="🛠️ Parts to Replace – Select only if repairs are needed due to damage or abnormal use">
            {Object.entries(groupedParts)
              .sort(
                ([a], [b]) =>
                  parseInt(a.match(/\d+/)?.[0] || "0", 10) -
                  parseInt(b.match(/\d+/)?.[0] || "0", 10)
              )
              .map(([groupCode, parts]) => {
                const groupName = parts[0]?.groupName || "";
                const isOpen = expandedGroups[groupCode];
                return (
                  <div key={groupCode} className="mb-4">
                    <button
                      type="button"
                      onClick={() => toggleGroup(groupCode)}
                      className={`w-full flex items-center justify-between bg-gray-100 hover:bg-gray-200 focus:bg-gray-100 active:bg-gray-200 focus:outline-none focus:ring-0 px-4 py-2 rounded-md transition-colors`}
                    >
                      <span className="font-semibold text-gray-800">
                        {groupCode} – {groupName}
                      </span>
                      <span
                        className={`transform transition-transform ${
                          isOpen ? "rotate-90" : ""
                        }`}
                      >
                        ▶
                      </span>
                    </button>
                    {isOpen && (
                      <div className="mt-3 space-y-3">
                        {parts.map((part) => {
                          const sel = selectedParts.find(
                            (p) => p.productNo === part.productNo
                          );
                          return (
                            <div
                              key={part.productNo}
                              className="flex items-center bg-gray-50 hover:bg-white border border-gray-200 rounded-md p-2"
                            >
                              <input
                                type="checkbox"
                                id={part.productNo}
                                className="form-checkbox h-5 w-5 accent-joule border-gray-300 rounded"
                                checked={!!sel}
                                onChange={(e) =>
                                  handlePartSelection(
                                    part,
                                    e.target.checked ? 1 : 0
                                  )
                                }
                              />
                              <label
                                htmlFor={part.productNo}
                                className="ml-3 flex-1 text-gray-700"
                              >
                                {part.productName}
                              </label>
                              {sel && (
                                <Counter
                                  value={sel.quantity}
                                  onChange={(qty) =>
                                    handlePartSelection(part, qty)
                                  }
                                  className="ml-2"
                                  min={1}
                                />
                              )}
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                );
              })}
          </SectionCard>

          <Divider />

          {/* ─────── Labour ─────── */}
          <SectionCard title="⏱️ Labour – Select only if repairs are needed due to damage or abnormal use">
            <div className="mb-6">
              <p className="text-gray-700 font-medium mb-4">Select minutes</p>
              <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 justify-center place-items-center gap-x-2 gap-y-4">
                {labourOptions.map((opt) => (
                  <PillButton
                    key={opt}
                    active={labourHours === opt}
                    onClick={() => setLabourHours(opt)}
                  >
                    {`${Math.round(opt * 60)} min`}
                  </PillButton>
                ))}
              </div>
            </div>

            <div>
              <label
                htmlFor="labourNote"
                className="block text-gray-700 text-sm font-semibold mt-2 mb-2"
              >
                Labour Note
              </label>
              <textarea
                id="labourNote"
                value={labourNote}
                onChange={(e) => setLabourNote(e.target.value)}
                placeholder="Describe the work that needs to be done..."
                className="border-gray-600 shadow-sm appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:border-joule focus:ring-1 focus:ring-joule"
              />
            </div>
          </SectionCard>

          <Divider />

          {/* ─────── actions ─────── */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <button
              type="submit"
              disabled={isSubmitDisabled}
              className={`w-full py-3 px-8 text-lg font-semibold rounded-lg shadow-md focus:outline-none ${
                isSubmitDisabled
                  ? "bg-gray-500 text-white cursor-not-allowed"
                  : "bg-joule text-white hover:bg-gray-900"
              }`}
            >
              {isSubmitting ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                  Loading…
                </>
              ) : (
                "Mark as Returned"
              )}
            </button>
            <button
              type="button"
              className="w-full py-3 px-8 text-lg font-semibold rounded-lg shadow-md bg-red-500 text-white hover:bg-red-700 focus:outline-none"
              onClick={toggleModalVisibility}
            >
              Cancel
            </button>
          </div>
        </form>
      </div>
    </ReactModal>
  );
};

export default ReturnConfirmationModal;
